import {
  ArrowRightLeft,
  Ban,
  CalendarClock,
  CheckCircle2,
  CircleDot,
  Coins,
  KeyRound,
  Mail,
  MailX,
  Palette,
  Pencil,
  RotateCcw,
  Settings,
  ShieldCheck,
  MessageSquareWarning,
  StickyNote,
  Trash2,
  UserCheck,
  UserPlus,
  XCircle,
} from "lucide-react";
import { AUDIT_EVENT_TYPES, type AuditEvent, type AuditEventType } from "@salesboard/contracts";

/** What a row needs to phrase itself: the values it carries, not the whole event. */
export type EventValues = Pick<AuditEvent, "oldValue" | "newValue">;

/**
 * The catalogue keys this registry may point at.
 *
 * Derived from the event vocabulary — `audit.${AuditEventType}` — plus the two readings of
 * `notes_changed` that depend on the row's values. Typed rather than `string` so a key that
 * does not exist in the catalogue fails `tsc -b` here, which keeps the registry's original
 * guarantee intact: an event can never render as a raw enum value in front of a user, and now
 * it can never render as a missing translation either.
 */
export type AuditLabelKey = `audit.${AuditEventType}` | "audit.notes_added" | "audit.notes_removed";

/**
 * The same event, worded so the record it happened to can follow the verb.
 *
 * The company-wide feed needs "created opportunity «X»" where a record's own history needs
 * "created this opportunity" — one is about something, the other is inside it. Mirrored key by
 * key rather than derived at random: `feedLabelKey` is the only way to cross between them, so
 * a `feed.*` string can never go missing for an event that has an `audit.*` one.
 */
export type FeedLabelKey = `feed.${AuditEventType}` | "feed.notes_added" | "feed.notes_removed";

export function feedLabelKey(key: AuditLabelKey): FeedLabelKey {
  return key.replace("audit.", "feed.") as FeedLabelKey;
}

export type EventPresentation = {
  icon: typeof Pencil;
  /**
   * The message key for what the row says. Reads after the actor's name — "Hana **changed the
   * name**" in English, "Hana — **změna názvu**" in Czech, where the verb would otherwise have
   * to agree with a gender no audit row records. See `audit.actorSeparator`.
   */
  labelKey: AuditLabelKey;
  /**
   * A label that depends on the values the row carries.
   *
   * One event type, three readings: a note that arrived, one that was rewritten, one that
   * was removed are the same `notes_changed` row distinguished only by which side is null.
   * Phrasing that in the registry keeps the timeline free of per-type branching.
   */
  labelKeyFor?: (values: EventValues) => AuditLabelKey;
  /** Whether to render an old → new value pair. */
  showValues: boolean;
  tone?: "neutral" | "positive" | "negative" | "warning";
};

/**
 * One registry mapping every event type to how it reads — bd-admin's single-registry
 * idea, which is the part of its activity log most worth keeping.
 *
 * Exhaustive by type: adding an event to the contract without describing it here fails
 * `tsc -b`, so an event can never render as a raw enum value in front of a user.
 */
export const EVENT_PRESENTATION: Record<AuditEventType, EventPresentation> = {
  [AUDIT_EVENT_TYPES.created]: { icon: CircleDot, labelKey: "audit.created", showValues: false },
  [AUDIT_EVENT_TYPES.clientChanged]: { icon: ArrowRightLeft, labelKey: "audit.client_changed", showValues: true },
  [AUDIT_EVENT_TYPES.nameChanged]: { icon: Pencil, labelKey: "audit.name_changed", showValues: true },
  [AUDIT_EVENT_TYPES.nipChanged]: { icon: Coins, labelKey: "audit.nip_changed", showValues: true },
  [AUDIT_EVENT_TYPES.expectedCloseDateChanged]: {
    icon: CalendarClock,
    labelKey: "audit.expected_close_date_changed",
    showValues: true,
  },
  [AUDIT_EVENT_TYPES.stageChanged]: { icon: ArrowRightLeft, labelKey: "audit.stage_changed", showValues: true },
  [AUDIT_EVENT_TYPES.closedWon]: {
    icon: CheckCircle2,
    labelKey: "audit.closed_won",
    showValues: true,
    tone: "positive",
  },
  [AUDIT_EVENT_TYPES.closedLost]: {
    icon: XCircle,
    labelKey: "audit.closed_lost",
    showValues: true,
    tone: "negative",
  },
  [AUDIT_EVENT_TYPES.reopened]: { icon: RotateCcw, labelKey: "audit.reopened", showValues: true, tone: "warning" },
  [AUDIT_EVENT_TYPES.ownerReassigned]: { icon: UserCheck, labelKey: "audit.owner_reassigned", showValues: true },
  [AUDIT_EVENT_TYPES.notesChanged]: {
    icon: StickyNote,
    labelKey: "audit.notes_changed",
    labelKeyFor: ({ oldValue, newValue }) =>
      oldValue === null ? "audit.notes_added" : newValue === null ? "audit.notes_removed" : "audit.notes_changed",
    showValues: true,
  },
  [AUDIT_EVENT_TYPES.lostReasonChanged]: { icon: MessageSquareWarning, labelKey: "audit.lost_reason_changed", showValues: true },
  [AUDIT_EVENT_TYPES.deleted]: { icon: Trash2, labelKey: "audit.deleted", showValues: false, tone: "negative" },
  [AUDIT_EVENT_TYPES.restored]: { icon: RotateCcw, labelKey: "audit.restored", showValues: false, tone: "warning" },

  [AUDIT_EVENT_TYPES.userInvited]: { icon: UserPlus, labelKey: "audit.user_invited", showValues: false },
  [AUDIT_EVENT_TYPES.invitationResent]: { icon: Mail, labelKey: "audit.invitation_resent", showValues: false },
  [AUDIT_EVENT_TYPES.invitationRevoked]: {
    icon: MailX,
    labelKey: "audit.invitation_revoked",
    showValues: false,
    tone: "warning",
  },
  // The record that a named person took control of the account.
  [AUDIT_EVENT_TYPES.credentialCreated]: {
    icon: KeyRound,
    labelKey: "audit.credential_created",
    showValues: false,
    tone: "positive",
  },
  [AUDIT_EVENT_TYPES.passwordReset]: { icon: KeyRound, labelKey: "audit.password_reset", showValues: false },
  [AUDIT_EVENT_TYPES.roleChanged]: { icon: ShieldCheck, labelKey: "audit.role_changed", showValues: true },
  [AUDIT_EVENT_TYPES.userDeactivated]: {
    icon: Ban,
    labelKey: "audit.user_deactivated",
    showValues: false,
    tone: "negative",
  },
  [AUDIT_EVENT_TYPES.colorChanged]: { icon: Palette, labelKey: "audit.color_changed", showValues: true },

  // Configuration. Phrased as what changed for everyone rather than as a record edit,
  // because these reshape the board rather than one deal.
  [AUDIT_EVENT_TYPES.stageCreated]: { icon: CircleDot, labelKey: "audit.stage_created", showValues: false },
  [AUDIT_EVENT_TYPES.stageRenamed]: { icon: Pencil, labelKey: "audit.stage_renamed", showValues: true },
  [AUDIT_EVENT_TYPES.stageTypeChanged]: {
    icon: ArrowRightLeft,
    labelKey: "audit.stage_type_changed",
    showValues: true,
    tone: "warning",
  },
  [AUDIT_EVENT_TYPES.stageColorChanged]: { icon: Palette, labelKey: "audit.stage_color_changed", showValues: true },
  [AUDIT_EVENT_TYPES.stageReordered]: { icon: ArrowRightLeft, labelKey: "audit.stage_reordered", showValues: true },
  [AUDIT_EVENT_TYPES.stageDeactivated]: {
    icon: Ban,
    labelKey: "audit.stage_deactivated",
    showValues: false,
    tone: "negative",
  },
  [AUDIT_EVENT_TYPES.stageReactivated]: { icon: RotateCcw, labelKey: "audit.stage_reactivated", showValues: false },
  [AUDIT_EVENT_TYPES.settingChanged]: {
    icon: Settings,
    labelKey: "audit.setting_changed",
    showValues: true,
    tone: "warning",
  },
  [AUDIT_EVENT_TYPES.userReactivated]: {
    icon: UserCheck,
    labelKey: "audit.user_reactivated",
    showValues: false,
    tone: "positive",
  },
};

/**
 * How one row reads. Pass the row's values when it has them, so a type whose phrasing
 * depends on them (a note added vs a note removed) can say which one happened.
 *
 * Returns a *key*, not a sentence. That is what lets two people read the same event in two
 * languages: the store holds values rather than prose, so the timeline resolves the phrasing
 * and formats the amounts and dates per reader.
 */
export function presentationFor(type: AuditEventType, values?: EventValues): EventPresentation {
  const presentation = EVENT_PRESENTATION[type];
  if (!presentation.labelKeyFor || !values) return presentation;
  return { ...presentation, labelKey: presentation.labelKeyFor(values) };
}
